/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ProductCard_Key as ProductCardKey } from '__generated__/graphql-types';

import { Grid, PageGridContainer, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import CampaignCollectionsModuleRenderer, {
  useShouldShowCampaignCollectionsModule,
} from 'bundles/campaigns/module-renderers/CampaignCollectionsModuleRenderer';
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import CollectionsContainer from 'bundles/collections-common/components/CollectionsContainer';
import SpotlightCollections from 'bundles/front-page/components/modules/collections/SpotlightCollections';
import getSpotlightCollectionsData from 'bundles/front-page/utils/getSpotlightCollectionsData';
import { PRODUCTCARD_CANONICAL_TYPENAMES } from 'bundles/product-card/constants';
import { mapProductToCanonicalType } from 'bundles/product-card/utils/productCardUtils';

import _t from 'i18n!nls/front-page';

type Props = {
  carouselCollections: CarouselCollection[];
  collectionsLoading?: boolean;
};

const styles = {
  disclaimer: css`
    margin-bottom: var(--cds-spacing-600);
  `,
};

// to prevent additional and non supported canonical types from being added to the API call
const supportedCanonicalTypes = [
  PRODUCTCARD_CANONICAL_TYPENAMES.SPECIALIZATION,
  PRODUCTCARD_CANONICAL_TYPENAMES.COURSE,
];

export function getProductCardKeysFromCollection(collection: CarouselCollection) {
  const productCardKeys: ProductCardKey[] = [];

  collection?.entities?.forEach((entity) => {
    const canonicalType = mapProductToCanonicalType(entity.productType);
    if (supportedCanonicalTypes.includes(canonicalType)) {
      const key = { id: entity.id, canonicalType };
      productCardKeys.push(key);
    }
  });

  return productCardKeys;
}

const Collections = ({ carouselCollections, collectionsLoading }: Props) => {
  const trackV3 = useTracker();
  const handleSpotlightCtaClick = () => {
    const trackV3Link = getSpotlightCollectionsData().ctaTarget;
    trackV3('click_button', {
      button: {
        name: 'view_all_products',
        linkName: 'spotlight_collection_cta_click',
        linkURL: trackV3Link,
        linkType: 'internal',
      },
    });
  };

  // Google Refresh Spotlight
  const additionalComponents = [
    {
      component: useShouldShowCampaignCollectionsModule() ? (
        <CampaignCollectionsModuleRenderer />
      ) : (
        <SpotlightCollections onClick={handleSpotlightCtaClick} {...getSpotlightCollectionsData()} />
      ),
      indexToInsert: 1,
    },
  ];

  return (
    <PageGridContainer>
      <Grid item xs={12}>
        <CollectionsContainer
          collections={carouselCollections}
          isLegacyCollection
          isCollectionsLoading={collectionsLoading}
          additionalComponents={additionalComponents}
        />
      </Grid>
      <Typography2 align="left" variant="bodySecondary" component="p" css={styles.disclaimer}>
        {_t(
          '¹Each university determines admission and the number of pre-approved prior learning credits that may count toward the degree requirements according to institutional policies, which may consider any existing credits you may have.'
        )}
      </Typography2>
    </PageGridContainer>
  );
};

export default Collections;
